import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
// Import Google icon
import googleIcon from '../assets/google-icon.svg';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/');
    } catch (error) {
      let errorMessage = 'Failed to log in';
      
      if (error.code === 'auth/invalid-credential') {
        errorMessage = 'Incorrect email or password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email. Need an account? Sign up instead.';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many unsuccessful login attempts. Please try again later or reset your password.';
      } else {
        errorMessage += ': ' + error.message;
      }
      
      setError(errorMessage);
    }
    setLoading(false);
  }
  
  async function handleGoogleSignIn() {
    try {
      setError('');
      setGoogleLoading(true);
      await signInWithGoogle();
      navigate('/');
    } catch (error) {
      let errorMessage = 'Failed to sign in with Google';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Sign-in popup was blocked by your browser. Please allow popups for this site.';
      } else {
        errorMessage += ': ' + error.message;
      }
      
      setError(errorMessage);
    }
    setGoogleLoading(false);
  }

  return (
    <div className="container">
      {/* Header with logo that links back to landing page */}
      <div className="row mt-4 mb-3">
        <div className="col">
          <Link to="/" className="text-decoration-none">
            <h4 className="text-light d-flex align-items-center">
              <i className="bi bi-house-door me-2"></i>
              <span>RE Calc</span>
            </h4>
          </Link>
        </div>
      </div>
      
      <div className="row justify-content-center min-vh-75 align-items-center">
        <div className="col-12 col-md-6 col-lg-4">
          <div className="card bg-dark border-secondary">
            <div className="card-body p-4">
              <h2 className="text-center mb-4">Log In</h2>
              
              {error && (
                <div className="alert alert-danger" role="alert" style={{ whiteSpace: 'pre-line' }}>
                  {error}
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label text-muted">Email</label>
                  <input
                    type="email"
                    className="form-control bg-dark text-light border-secondary"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>
                
                <div className="mb-3">
                  <label className="form-label text-muted">Password</label>
                  <input
                    type="password"
                    className="form-control bg-dark text-light border-secondary"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                  />
                </div>
                
                <button 
                  type="submit" 
                  className="btn btn-primary w-100 mb-3" 
                  disabled={loading || googleLoading || !email.trim() || !password.trim()}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Logging in...
                    </>
                  ) : (
                    'Log In'
                  )}
                </button>
                
                <div className="d-flex align-items-center mb-3">
                  <hr className="flex-grow-1 border-secondary" />
                  <span className="mx-2 text-muted">or</span>
                  <hr className="flex-grow-1 border-secondary" />
                </div>
                
                <button 
                  type="button" 
                  className="btn btn-outline-light w-100 d-flex align-items-center justify-content-center" 
                  onClick={handleGoogleSignIn}
                  disabled={loading || googleLoading}
                >
                  {googleLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Signing in...
                    </>
                  ) : (
                    <>
                      <img src={googleIcon} alt="Google" className="me-2" style={{ width: '18px', height: '18px' }} />
                      Sign in with Google
                    </>
                  )}
                </button>
              </form>
              
              <div className="d-flex justify-content-between align-items-center mt-3">
                <small className="text-muted">
                  <Link to="/forgot-password" className="text-primary text-decoration-none">Forgot Password?</Link>
                </small>
                
                <small className="text-muted">
                  Need an account? <Link to="/signup" className="text-primary text-decoration-none">Sign Up</Link>
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
